import {MBTilesUtil} from '../../utils/mbtiles.util';

/**
 * An MBTiles file whose tiles are vector data (pbf) rather than pictures: the
 * map can only draw images, so every tile used to come out as a broken image
 * while the layer looked loaded.
 */
export class MBTilesFormatError extends Error {
  constructor(readonly format: string) {
    super(`MBTiles file with ${format} (vector) tiles cannot be drawn`);
    this.name = 'MBTilesFormatError';
  }
}

/** The image types an MBTiles `format` names (the spec's png, jpg, webp; jpeg and gif seen in the wild). */
const RASTER_FORMATS: {[format: string]: string} = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
};
const VECTOR_FORMATS = ['pbf', 'mvt'];

export class MBTilesProvider implements google.maps.MapType {

  /**
   * Every provider whose database is open. sql.js keeps each database in its
   * own in-memory file system for as long as the page lives, so one that is
   * dropped without close() pins a whole copy of the file: reopening a
   * project with a 300 MB layer a few times could run the tab out of memory.
   */
  private static open = new Set<MBTilesProvider>();

  /** Closes the databases of a layer's providers (the layer was removed). */
  static disposeLayer(id: string) {
    [...MBTilesProvider.open].filter(p => p.id === id).forEach(p => p.dispose());
  }

  /** Closes every open database (the map's layers were all cleared). */
  static disposeAll() {
    [...MBTilesProvider.open].forEach(p => p.dispose());
  }

  /** Shows a layer's new opacity on its tiles, the ones on screen included. */
  static setLayerOpacity(id: string, opacity: number) {
    MBTilesProvider.open.forEach(p => {
      if (p.id === id) {
        p.setOpacity(opacity);
      }
    });
  }

  db: any;
  id: string;
  metadata: any;

  alt = 'MBTiles Map Type';
  maxZoom = 27;
  minZoom = 2;
  name = 'MBTiles Map Type';
  projection = null;
  radius = 6378137;
  tileSize: google.maps.Size;
  // The layer's opacity (its Transparency slider), which was never handed
  // over: the tiles always drew fully opaque.
  opacity = 1;

  // The deepest level the file has tiles for. Google ignores maxZoom on an
  // overlay and keeps asking for deeper tiles; those stretch this level's.
  private deepestZoom: number | null;

  // The type the tiles' bytes are labelled with.
  private mimeType: string;

  // Each tile's blob URL pins a copy of the tile bytes until revoked: once
  // its image has loaded, or when Google drops the tile before that.
  private tileUrls = new WeakMap<Element, string>();

  // The tiles Google is showing (until releaseTile), so a new opacity
  // reaches them without waiting for them to be redrawn.
  private liveTiles = new Set<HTMLElement>();

  /**
   * Takes over the database: it is closed by dispose(), or straight away when
   * the file is refused here (no metadata, or vector tiles -- MBTilesFormatError).
   */
  constructor(db, id, opacity = 1) {
    try {
      this.metadata = MBTilesUtil.getAllMetadata(db);
      this.mimeType = MBTilesProvider.findTileType(db, this.metadata);
    } catch (e) {
      MBTilesProvider.closeDatabase(db);
      throw e;
    }
    this.tileSize = new google.maps.Size(256, 256);
    this.minZoom = this.metadata.minzoom;
    this.maxZoom = this.metadata.maxzoom;
    this.db = db;
    this.id = id;
    this.alt = id;
    this.name = this.metadata.name;
    this.deepestZoom = MBTilesProvider.findDeepestZoom(db, this.metadata);
    this.opacity = MBTilesProvider.toOpacity(opacity);
    MBTilesProvider.open.add(this);
  }

  /** Sets the tiles' opacity, those already on the map included. */
  setOpacity(opacity: number) {
    this.opacity = MBTilesProvider.toOpacity(opacity);
    this.liveTiles.forEach(tile => tile.style.opacity = `${this.opacity}`);
  }

  /**
   * Closes the database, freeing its copy of the file. Called once the
   * provider is off the map; a tile asked for afterwards comes back empty.
   */
  dispose() {
    MBTilesProvider.open.delete(this);
    this.liveTiles.forEach(tile => this.revokeTileUrl(tile));
    this.liveTiles.clear();
    const db = this.db;
    this.db = null;
    MBTilesProvider.closeDatabase(db);
  }

  private static closeDatabase(db) {
    try {
      db?.close();
    } catch (e) {
      // Already closed.
    }
  }

  /** 0..1; a missing or unreadable value (older layers) is fully opaque. */
  private static toOpacity(value: number): number {
    const opacity = Number(value);
    return Number.isFinite(opacity) ? Math.min(1, Math.max(0, opacity)) : 1;
  }

  /**
   * The image type of the file's tiles. Vector tiles (format pbf, or gzipped
   * or bare protobuf bytes when the optional format is missing) cannot be
   * drawn, so the file is refused instead of showing nothing without a word.
   */
  private static findTileType(db, metadata): string {
    const declared = String(metadata.format ?? '').trim().toLowerCase();
    if (VECTOR_FORMATS.includes(declared)) {
      throw new MBTilesFormatError(declared);
    }
    const sniffed = MBTilesProvider.sniffTile(db);
    if (sniffed === 'vector') {
      throw new MBTilesFormatError(declared || 'pbf');
    }
    return sniffed || RASTER_FORMATS[declared] || 'image/png';
  }

  /** What the first tile's bytes are: an image type, 'vector', or null when they do not say. */
  private static sniffTile(db): string | null {
    let bytes: unknown;
    try {
      const res = db.exec('SELECT tile_data FROM tiles LIMIT 1');
      bytes = res.length > 0 ? res[0].values[0][0] : null;
    } catch (e) {
      return null;
    }
    if (!(bytes instanceof Uint8Array) || bytes.length < 4) {
      return null;
    }
    const b = bytes;
    if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) {
      return 'image/png';
    }
    if (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) {
      return 'image/jpeg';
    }
    if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46
      && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) {
      return 'image/webp';
    }
    if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38) {
      return 'image/gif';
    }
    // gzip or zlib (pictures are never compressed again), or a bare vector
    // tile, whose first field is a layer (protobuf tag 0x1A).
    const zlib = b[0] === 0x78 && [0x01, 0x5E, 0x9C, 0xDA].includes(b[1]);
    if ((b[0] === 0x1F && b[1] === 0x8B) || zlib || b[0] === 0x1A) {
      return 'vector';
    }
    return null;
  }

  /** Metadata values are TEXT and maxzoom is optional, so the tiles table answers when it is missing. */
  private static findDeepestZoom(db, metadata): number | null {
    const declared = parseInt(metadata.maxzoom, 10);
    if (Number.isFinite(declared)) {
      return declared;
    }
    try {
      const res = db.exec('SELECT MAX(zoom_level) FROM tiles');
      const found = res.length > 0 ? res[0].values[0][0] : null;
      const level = found === null || found === undefined ? NaN : Number(found);
      return Number.isFinite(level) ? level : null;
    } catch (e) {
      return null;
    }
  }

  getTile(coord: google.maps.Point, zoom: number, ownerDocument: Document): Element {

    let x = coord.x;
    let y = coord.y;
    zoom = Math.round(zoom);

    const dim = Math.pow(2, zoom);

    // Columns arrive unwrapped across the antimeridian; wrap, never mirror.
    // Rows do not wrap, so off-world rows get an empty tile.
    x = ((x % dim) + dim) % dim;
    if (y < 0 || y >= dim) {
      return ownerDocument.createElement('div');
    }

    // Past the file's deepest level, show the part of the deepest tile that
    // covers this one, stretched: the imagery used to vanish exactly where
    // users zoom in to place points on it.
    let level = zoom;
    let scale = 1;
    let offsetX = 0;
    let offsetY = 0;
    if (this.deepestZoom !== null && zoom > this.deepestZoom) {
      level = this.deepestZoom;
      scale = Math.pow(2, zoom - level);
      const parentX = Math.floor(x / scale);
      const parentY = Math.floor(y / scale);
      offsetX = x - parentX * scale;
      offsetY = y - parentY * scale;
      x = parentX;
      y = parentY;
    }

    const nRows = (1 << level); // Num rows = 2^level
    y = nRows - 1 - y;

    let mbtilesUrl;
    if (this.db) {
      const data = MBTilesUtil.getTile(this.db, level, x, y);
      if (data.length > 0) {
        mbtilesUrl = this.arrayToDataUrl(data[0].values[0][0]);
      }
    }
    const div = ownerDocument.createElement('div');

    if (mbtilesUrl) {
      const th = this.tileSize.height;
      const tw = this.tileSize.width;

      div.style.opacity = `${this.opacity}`;
      div.style.position = 'relative';
      div.style.width = tw + 'px';
      div.style.height = th + 'px';
      div.style.overflow = 'hidden';
      div.style.backgroundColor = 'transparent';
      div.style.padding = '0';
      div.style.margin = '0';
      div.style.fontSize = '10';
      div.style.borderWidth = '0';
      div.style.borderColor = 'transparent';

      const img = ownerDocument.createElement('img');
      img.height = th * scale;
      img.width = tw * scale;
      img.style.cssText = `position: absolute; z-index: 2; top: ${-offsetY * th}px; left: ${-offsetX * tw}px;`;
      // A loaded image keeps showing after its URL is revoked.
      this.tileUrls.set(div, mbtilesUrl);
      const release = () => this.revokeTileUrl(div);
      img.onload = release;
      img.onerror = release;
      img.src = mbtilesUrl;

      div.appendChild(img);
      this.liveTiles.add(div);
    }
    /*var c = ownerDocument.createElement('div');
    c.style.cssText = "position: absolute; z-index: 2; top: 0; left: 0;";
    c.innerHTML = x + " - " + y;
    div.appendChild(c);*/
    return div;
  }

  getPosition() {
    const coords = this.metadata.center.split(',');
    return {lat: Number(coords[0]), lng: Number(coords[1])};
  }

  getBounds() {
    const coords = this.metadata.bounds.split(',');
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(new google.maps.LatLng(Number(coords[1]), Number(coords[0])));
    bounds.extend(new google.maps.LatLng(Number(coords[3]), Number(coords[2])));
    return bounds.toJSON();
  }

  arrayToDataUrl(array) {
    const blob = new Blob([array], {type: this.mimeType || 'image/png'});
    const urlCreator = window.URL || window['webkitURL'];
    const imageUrl = urlCreator.createObjectURL(blob);
    return imageUrl;
  }

  releaseTile(tile: Element): void {
    this.liveTiles.delete(tile as HTMLElement);
    this.revokeTileUrl(tile);
  }

  private revokeTileUrl(tile: Element) {
    const url = this.tileUrls.get(tile);
    if (url) {
      this.tileUrls.delete(tile);
      (window.URL || window['webkitURL']).revokeObjectURL(url);
    }
  }

}
